import dayjs from "dayjs"
import { readAllData, STORES } from "utility"

// Statuts "actifs" en salle d'attente — alignés sur le backend
// (ACTIVE_WAITING_STATUSES : SCHEDULED_CHECKIN, CALLED, IN_CONSULTATION).
const ACTIVE_WAITING_STATUSES = new Set([
  "SCHEDULED_CHECKIN",
  "CALLED",
  "IN_CONSULTATION",
])

const getDate = (c) => c.creationDate || c.createdAt

// Backend : une consultation est payée quand remainingAmount === 0.
const isPaidConsultation = (c) =>
  Number(c.remainingAmount) === 0 ||
  (c.remainingAmount == null && Number(c.amountPaid) > 0)

export async function getStatisticsOffline() {
  const [patients, consultations, appointments, reminders] = await Promise.all([
    readAllData(STORES.PATIENTS),
    readAllData(STORES.CONSULTATIONS),
    readAllData(STORES.APPOINTMENTS),
    readAllData(STORES.REMINDERS),
  ])

  const now = dayjs()
  const thisMonth = now.month()
  const thisYear = now.year()
  const todayStr = now.format("YYYY-MM-DD")

  const inToday = (c) => {
    const d = getDate(c)
    return d && dayjs(d).format("YYYY-MM-DD") === todayStr
  }
  const inMonth = (c) => {
    const d = getDate(c)
    if (!d) return false
    const dd = dayjs(d)
    return dd.month() === thisMonth && dd.year() === thisYear
  }
  const inYear = (c) => {
    const d = getDate(c)
    return d && dayjs(d).year() === thisYear
  }

  const consToday = consultations.filter(inToday)
  const consMonth = consultations.filter(inMonth)
  const consYear = consultations.filter(inYear)

  return {
    patients: patients.length,
    appointments: appointments.length,
    reminders: reminders.length,
    totalConsultationsDay: consToday.length,
    totalConsultationsMonth: consMonth.length,
    totalConsultationsYear: consYear.length,
    paidConsultationsDay: consToday.filter(isPaidConsultation).length,
    paidConsultationsMonth: consMonth.filter(isPaidConsultation).length,
    paidConsultationsYear: consYear.filter(isPaidConsultation).length,
    // Pas de store de réclamations hors ligne — on renvoie 0 par défaut.
    claims: { total: 0, validated: 0, nonValidated: 0 },
  }
}

export async function getChartDataOffline() {
  const payments = await readAllData(STORES.PAYMENTS)
  const now = dayjs()
  const result = []

  for (let i = 11; i >= 0; i--) {
    const m = now.subtract(i, "month")
    const monthPayments = payments.filter((p) => {
      const d = p.creationDate || p.date || p.createdAt
      if (!d) return false
      const dd = dayjs(d)
      return dd.month() === m.month() && dd.year() === m.year()
    })
    const totalPayments = monthPayments.reduce(
      (sum, p) => sum + (Number(p.payment) || Number(p.montant) || 0),
      0,
    )
    result.push({
      month: m.format("MMM"),
      // Pas de store de dépenses hors ligne → expenses = 0,
      // donc chiffreAffaire ≈ total des paiements.
      expenses: 0,
      payments: totalPayments,
      chiffreAffaire: totalPayments,
    })
  }

  return result
}

export async function getChiffreAffaireOffline({ filters = {} } = {}) {
  const { type, month, year } = filters
  const payments = await readAllData(STORES.PAYMENTS)

  let start
  let end
  if (type === "annee" && year) {
    start = dayjs(`${year}-01-01`).startOf("year")
    end = dayjs(`${year}-01-01`).endOf("year")
  } else if (month && year) {
    start = dayjs(`${year}-${month}-01`).startOf("month")
    end = dayjs(`${year}-${month}-01`).endOf("month")
  } else {
    start = dayjs().startOf("month")
    end = dayjs().endOf("month")
  }

  const startMs = start.valueOf()
  const endMs = end.valueOf()
  const total = payments
    .filter((p) => {
      const d = p.creationDate || p.date || p.createdAt
      if (!d) return false
      const ms = dayjs(d).valueOf()
      return ms >= startMs && ms <= endMs
    })
    .reduce((sum, p) => sum + (Number(p.payment) || Number(p.montant) || 0), 0)

  // expenses non disponibles hors ligne → chiffreAffaire ≈ paiements
  return {
    type: type || "mois",
    month: month ? Number(month) : null,
    year: year ? Number(year) : dayjs().year(),
    chiffreAffaire: total,
  }
}

export async function getWaitingMetricsTodayOffline() {
  const waitingList = await readAllData(STORES.WAITING_LIST)

  const normalize = (s) => (s ? String(s).toUpperCase() : "")
  const waitingRoomCount = waitingList.filter((w) =>
    ACTIVE_WAITING_STATUSES.has(normalize(w.status)),
  ).length

  return { waitingRoomCount }
}
